import { GeminiErrorResponse, GeminiResponse } from '../types/gemini';

// TODO: Move API Key to environment variables for security
const GEMINI_API_KEY = 'AIzaSyAQf3Cw6vjImOywSv2NotXnaJHBu8Mya8A'; // Replace with process.env.VITE_GEMINI_API_KEY
const GEMINI_API_ENDPOINT = 'https://generativelanguage.googleapis.com/v1beta/models/gemini-2.5-flash-preview-04-17:generateContent';

/**
 * Converts a Blob to a base64 string.
 * @param blob The image blob.
 * @returns A promise that resolves with the base64 string (without the data URL prefix).
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const base64data = reader.result as string;
            // Remove the data URL prefix (e.g., "data:image/jpeg;base64,")
            const base64 = base64data.split(',')[1];
            if (base64) {
                resolve(base64);
            } else {
                reject(new Error('Failed to convert blob to base64.'));
            }
        };
        reader.onerror = (error) => {
            reject(error);
        };
        reader.readAsDataURL(blob);
    });
};

/**
 * Generates descriptive tags for an image using the Gemini API.
 * @param imageBlob The image Blob to analyze.
 * @returns A promise that resolves with an array of tag strings.
 */
export const generateTagsWithGemini = async (imageBlob: Blob): Promise<string[]> => {
    try {
        const base64Image = await blobToBase64(imageBlob);

        const prompt = `**Role:** You are an expert food analyst specializing in generating concise, relevant tags for categorization and search.

**Task:** Analyze the provided image, which primarily features food. Generate a comma-separated list of up to 10 highly relevant and descriptive tags. Provide the highest relevance and most descriptive tags.

**Tagging Guidelines:**
*   **Focus exclusively on the food itself.** Identify specific dishes (e.g., spaghetti carbonara, beef pho), main ingredients (e.g., salmon, avocado, mushroom), and cuisine styles (e.g., italian, japanese, mexican - if obvious)
*   **Be Specific:** Prefer "margherita pizza" over just "pizza". Prefer "ramen noodles" over just "noodles".
*   **Use single words or very short phrases** (2-3 words max).
*   **Do not duplicate tags or very similar tags** (e.g., "pizza" and "margherita pizza").
*   **Format:** Provide ONLY the comma-separated list of tags. Absolutely no introductory text, no explanations, no bullet points, no numbering, no apologies if unsure.

**Negative Constraints (Strictly Avoid):**
*   Do NOT describe non-food items (plates, cutlery, napkins, tables, backgrounds, people, hands).
*   Do NOT include overly generic or subjective terms like 'food', 'meal', 'dish', 'plate', 'delicious', 'tasty', 'appetizing', 'yummy', 'restaurant', 'eating', 'drink', 'beverage', 'salt', 'pepper', 'oil', 'water', 'healthy', 'unhealthy'.
*   Do NOT describe image quality, lighting, or composition.
*   Do NOT use full sentences or descriptive paragraphs.
* 
**Now, analyze the following image and provide ONLY the comma-separated tags:**`;

        const response = await fetch(`${GEMINI_API_ENDPOINT}?key=${GEMINI_API_KEY}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                contents: [
                    {
                        parts: [
                            {
                                text: prompt
                            },
                            {
                                inline_data: {
                                    mime_type: imageBlob.type,
                                    data: base64Image
                                }
                            }
                        ]
                    }
                ],
                generationConfig: {
                    temperature: 0.5
                }
            })
        });

        if (!response.ok) {
            const errorData = await response.json() as GeminiErrorResponse;
            console.error('Gemini API Error Response:', errorData);
            throw new Error(errorData.error?.message || `Gemini API request failed with status ${response.status}`);
        }

        const geminiData = await response.json() as GeminiResponse;

        if (!geminiData.candidates || geminiData.candidates.length === 0 || !geminiData.candidates[0].content?.parts || geminiData.candidates[0].content.parts.length === 0) {
            console.error('Invalid Gemini API response structure:', geminiData);
            throw new Error('Failed to parse tags from Gemini response. Invalid structure.');
        }

        // Remove trim() from the raw response text
        const tagsText = geminiData.candidates[0].content.parts[0].text; 

        // Split by comma and remove trim() from each tag mapping
        const tags = tagsText.split(',').map((tag: string) => tag).filter((tag: string) => tag.length > 0);

        return tags;

    } catch (error) {
        console.error('Error generating tags with Gemini:', error);
        // Re-throw the error to be handled by the caller
        throw error;
    }
}; 